import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import { boardService } from '../../../../lib/api/boards';
import { useAlertStore } from '../../../../stores/alertStore';

interface FileInfo {
    key: string;
    name: string;
    size: number;
    mimeType: string;
    uploadedAt: string;
}

interface OperationFileExplorerModalProps {
    isOpen: boolean;
    onClose: () => void;
    journalId: string;
    operationId: string;
}

export const OperationFileExplorerModal: React.FC<OperationFileExplorerModalProps> = ({
    isOpen,
    onClose,
    journalId,
    operationId
}) => {
    const { t } = useTranslation('common');
    const { showAlert } = useAlertStore();
    const queryClient = useQueryClient();
    const [files, setFiles] = useState<FileInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (isOpen && operationId) {
            fetchFiles();
        }
    }, [isOpen, operationId]);

    const fetchFiles = async () => {
        setIsLoading(true);
        try {
            const data = await boardService.getOperationFiles(journalId, operationId);
            setFiles(data);
        } catch (error) {
            console.error('Failed to fetch files', error);
            showAlert({ type: 'error', message: t('modals.operationFileExplorer.alerts.loadError') });
        } finally {
            setIsLoading(false);
        }
    };

    const onDrop = async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;
        setIsUploading(true);
        try {
            for (const file of acceptedFiles) {
                await boardService.addOperationFile(journalId, operationId, file);
            }
            await fetchFiles();
            
            // Invalidate operation queries to refresh 'hasFile' status in the list
            queryClient.invalidateQueries({ queryKey: ['journal-operations'] });
            
            showAlert({ type: 'success', message: t('modals.operationFileExplorer.alerts.uploadSuccess') });
        } catch (error) {
            console.error('Upload failed', error);
            showAlert({ type: 'error', message: t('modals.operationFileExplorer.alerts.uploadError') });
        } finally {
            setIsUploading(false);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        noClick: true // We have a separate button for click to avoid conflict with list items
    });

    const handleDelete = async (fileKey: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await boardService.deleteOperationFile(journalId, operationId, fileKey);
            setFiles(prev => prev.filter(f => f.key !== fileKey));
            
            // Invalidate operation queries to refresh 'hasFile' status in the list
            queryClient.invalidateQueries({ queryKey: ['journal-operations'] });
        } catch (error) {
            showAlert({ type: 'error', message: t('modals.operationFileExplorer.alerts.deleteError') });
        }
    };

    const handleDownload = async (file: FileInfo) => {
        try {
            const blob = await boardService.getOperationFileUrl(journalId, operationId, file.key) as unknown as Blob;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            showAlert({ type: 'error', message: t('modals.operationFileExplorer.alerts.downloadError') });
        }
    };

    const filteredFiles = useMemo(() => {
        return files.filter(f => 
            f.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [files, searchTerm]);

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };


    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[100]"
                    />
                    
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.98, y: 10 }}
                            className="bg-white dark:bg-neutral-900 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] w-full max-w-xl h-[500px] flex flex-col overflow-hidden border border-neutral-100 dark:border-neutral-800 pointer-events-auto"
                        >
                            <div {...getRootProps()} className="flex flex-col flex-1 min-h-0">
                                <input {...getInputProps()} />
                            
                                {/* Compact Top Bar */}
                                <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 flex justify-between items-center bg-white dark:bg-neutral-900">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 flex items-center justify-center bg-neutral-50 dark:bg-neutral-800 rounded-lg text-neutral-400 flex-shrink-0">
                                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4f9e646c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4f9e646c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' t-ref="icon" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-bold text-neutral-600 dark:text-neutral-200 truncate">
                                                {t('modals.operationFileExplorer.title')}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={onClose}
                                            className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                                        >
                                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_2e46f7c6.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2e46f7c6.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    </div>
                                </div>

                                {/* Compact Actions Row */}
                                <div className="px-4 py-2 flex items-center gap-3 border-b border-neutral-50 dark:border-neutral-800 bg-neutral-50/20 dark:bg-neutral-800/10">
                                    <div className="relative flex-1">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_9c8789e0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_9c8789e0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                        <input 
                                            type="text"
                                            placeholder={t('modals.operationFileExplorer.searchPlaceholder')}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-9 pr-3 py-1.5 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg focus:ring-1 focus:ring-accent-primary outline-none transition-all placeholder:text-neutral-400 text-neutral-900 dark:text-neutral-100 text-xs"
                                        />
                                    </div>
                                    <label className={`flex-shrink-0 px-3 py-1.5 bg-accent-primary text-white rounded-lg text-xs font-bold hover:bg-accent-primary/90 transition-all cursor-pointer flex items-center gap-2 shadow-sm ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                        <input {...getInputProps({ multiple: true })} style={{ display: 'none' }} onChange={(e) => {
                                            if (e.target.files) onDrop(Array.from(e.target.files));
                                        }} />
                                        <span className="w-3.5 h-3.5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_db0168eb.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_db0168eb.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                        {isUploading ? t('modals.operationFileExplorer.uploading') : t('modals.operationFileExplorer.addFile')}
                                    </label>
                                </div>

                                {/* File List Area */}
                                <div className="flex-1 overflow-y-auto min-h-0 p-4 scrollbar-thin">
                                    {isLoading ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <div className="animate-spin w-6 h-6 border-2 border-neutral-100 border-t-accent-primary rounded-full mb-3" />
                                            <span className="text-xs font-medium text-neutral-400">{t('common.loading')}</span>
                                        </div>
                                    ) : filteredFiles.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-10 text-center">
                                            <div className="w-12 h-12 bg-neutral-50 dark:bg-neutral-800 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 hover:scale-110">
                                                <span className="w-6 h-6 text-neutral-300" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_31fe8b51.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_31fe8b51.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                            </div>
                                            <h4 className="text-base font-bold text-neutral-900 dark:text-neutral-100">{t('modals.operationFileExplorer.empty')}</h4>
                                            <p className="text-xs text-neutral-400 mt-1 max-w-[240px] mx-auto leading-relaxed">
                                                {searchTerm ? t('common.noResults') : t('modals.operationFileExplorer.emptyDesc')}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 gap-2">
                                            {filteredFiles.map((file) => (
                                                <motion.div 
                                                    layout
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    key={file.key}
                                                    className="group flex items-center p-3 bg-white dark:bg-neutral-800/40 border border-neutral-100 dark:border-neutral-800 hover:border-accent-primary/30 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-xl transition-all duration-200"
                                                >
                                                    <div className="w-9 h-9 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg flex items-center justify-center mr-3 group-hover:bg-accent-primary/10 transition-colors">
                                                       <span className="w-4 h-4 text-neutral-400 group-hover:text-accent-primary transition-colors" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_4f9e646c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4f9e646c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                                    </div>
                                                    <div className="flex-1 min-w-0 mr-3">
                                                        <h5 className="text-xs font-bold text-neutral-700 dark:text-neutral-200 truncate group-hover:text-accent-primary transition-colors">{file.name}</h5>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[10px] text-neutral-400 font-medium">{formatSize(file.size)}</span>
                                                            <span className="w-1 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
                                                            <span className="text-[10px] text-neutral-400">
                                                                {new Date(file.uploadedAt).toLocaleDateString(undefined, {
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                                            className="p-1.5 text-neutral-500 dark:text-neutral-400 hover:text-accent-primary hover:bg-accent-primary/10 rounded-lg transition-all border border-neutral-100 dark:border-neutral-700 hover:border-accent-primary/20 bg-neutral-50 dark:bg-neutral-800/50"
                                                            title={t('common.download')}
                                                        >
                                                            <span className="w-3.5 h-3.5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_97a87d0d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_97a87d0d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                                        </button>
                                                        <button 
                                                            onClick={(e) => handleDelete(file.key, e)}
                                                            className="p-1.5 text-neutral-500 dark:text-neutral-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-all border border-neutral-100 dark:border-neutral-700 hover:border-red-600/20 bg-neutral-50 dark:bg-neutral-800/50"
                                                            title={t('common.delete')}
                                                        >
                                                            <span className="w-3.5 h-3.5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_13e23959.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_13e23959.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Multi-file Drop Drop Indicator */}
                                <AnimatePresence>
                                    {isDragActive && (
                                        <motion.div 
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 bg-accent-primary/5 backdrop-blur-[1px] border-2 border-accent-primary/30 border-dashed rounded-2xl z-20 flex flex-col items-center justify-center pointer-events-none"
                                        >
                                            <div className="w-14 h-14 bg-accent-primary text-white rounded-full flex items-center justify-center shadow-lg animate-bounce">
                                                <span className="w-7 h-7" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_db0168eb.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_db0168eb.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} />
                                            </div>
                                            <p className="mt-3 text-sm font-bold text-accent-primary">
                                                {t('modals.addJournal.dropZone.title')}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Bottom Info Bar */}
                                <div className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800/30 border-t border-neutral-100 dark:border-neutral-800 flex justify-center">
                                     <p className="text-[9px] font-bold text-neutral-400 uppercase tracking-widest text-center opacity-70">
                                        {t('modals.operationFileExplorer.tip')}
                                     </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};
